import type { MensagemChat } from '@mymaxx/shared';
import { create } from 'zustand';

/** Uma janelinha do canal paralelo, presa a um atendimento. */
export interface ConversaSussurro {
  conversaId: string;
  protocolo: string;
  mensagens: MensagemChat[];
  /** Fechada = só a aba na barra de baixo; aberta = janela com o histórico. */
  aberta: boolean;
  naoLidas: number;
}

interface EstadoSussurros {
  conversas: ConversaSussurro[];
  /** Abre (ou traz para a frente) o canal daquele atendimento. */
  abrir: (conversaId: string, protocolo: string) => void;
  fechar: (conversaId: string) => void;
  minimizar: (conversaId: string) => void;
  /** Substitui o histórico depois de carregar do servidor. */
  carregar: (conversaId: string, mensagens: MensagemChat[]) => void;
  /** Mensagem nova: do socket (conta como não lida se a janela está fechada) ou minha. */
  receber: (
    conversaId: string,
    protocolo: string,
    mensagem: MensagemChat,
    opcoes?: { minha?: boolean },
  ) => void;
  limpar: () => void;
}

/**
 * As janelas de sussurro abertas no painel, fora de qualquer tela. O supervisor abre uma
 * do monitor; o atendente recebe uma quando alguém fala com ele — e ela continua lá
 * enquanto ele navega, porque a conversa é dele, não da página.
 */
export const useSussurros = create<EstadoSussurros>((set) => ({
  conversas: [],

  abrir: (conversaId, protocolo) =>
    set((e) => {
      const existe = e.conversas.some((c) => c.conversaId === conversaId);
      return {
        conversas: existe
          ? e.conversas.map((c) =>
              c.conversaId === conversaId ? { ...c, aberta: true, naoLidas: 0 } : c,
            )
          : [...e.conversas, { conversaId, protocolo, mensagens: [], aberta: true, naoLidas: 0 }],
      };
    }),

  fechar: (conversaId) =>
    set((e) => ({ conversas: e.conversas.filter((c) => c.conversaId !== conversaId) })),

  minimizar: (conversaId) =>
    set((e) => ({
      conversas: e.conversas.map((c) =>
        c.conversaId === conversaId ? { ...c, aberta: false } : c,
      ),
    })),

  carregar: (conversaId, mensagens) =>
    set((e) => ({
      conversas: e.conversas.map((c) => (c.conversaId === conversaId ? { ...c, mensagens } : c)),
    })),

  receber: (conversaId, protocolo, mensagem, opcoes) =>
    set((e) => {
      const atual = e.conversas.find((c) => c.conversaId === conversaId);
      if (!atual) {
        // Sussurro de um atendimento que eu ainda não tinha na tela: abre a janela.
        // Antes abria só a abinha piscando no rodapé, e o recado da supervisão passava
        // batido — que é o contrário do motivo de existir do canal (28/09). Abrir não
        // atrapalha quem está escrevendo: a janela não toma o cursor do campo.
        return {
          conversas: [
            ...e.conversas,
            { conversaId, protocolo, mensagens: [mensagem], aberta: true, naoLidas: 0 },
          ],
        };
      }
      // O socket pode devolver a mensagem que eu mesmo acabei de mandar pelo HTTP.
      if (atual.mensagens.some((m) => m.id === mensagem.id)) return e;
      return {
        conversas: e.conversas.map((c) =>
          c.conversaId === conversaId
            ? {
                ...c,
                mensagens: [...c.mensagens, mensagem],
                naoLidas: c.aberta || opcoes?.minha ? 0 : c.naoLidas + 1,
              }
            : c,
        ),
      };
    }),

  limpar: () => set({ conversas: [] }),
}));
